/*
 * /auth (page)
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { supabase } from "@psychology/auth/client"
import {
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Input,
	Label,
	Page,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "@psychology/react-ui"
import { AppTitle } from "@psychology/utils"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import z from "zod"
import { appConfig } from "../../config/app-config"
import { authPageLoaderSoft } from "../../utils/auth.utils"

const authSearchSchema = z.object({
	tab: z.enum(["login", "register"]).catch("login"),
})

export const Route = createFileRoute("/auth/")({
	component: AuthPage,
	beforeLoad: authPageLoaderSoft({
		allowUnauthenticated: true,
		redirectAuthorized: "/dashboard",
	}),
	validateSearch: authSearchSchema,
})

function AuthPage() {
	const { t } = useTranslation()
	const defaultTab = Route.useSearch({ select: ({ tab }) => tab })
	const navigate = useNavigate()

	const [loading, setLoading] = useState(false)
	const [email, setEmail] = useState("")
	const [password, setPassword] = useState("")
	const [displayName, setDisplayName] = useState("")
	const [error, setError] = useState<string | null>(null)

	// Handle login with email and password
	async function handleLogin(e: React.FormEvent) {
		e.preventDefault()

		try {
			setLoading(true)

			const { error } = await supabase.auth.signInWithPassword({
				email,
				password,
			})

			if (error) {
				throw error
			}

			toast.success(t("auth.loginSuccess"))
			navigate({ to: appConfig.redirects.afterSignInUrl })
		} catch (e) {
			const error = e as Error
			toast.error(error.message || t("auth.loginFailed"))
			setError(error.message || t("auth.loginFailed"))
		} finally {
			setLoading(false)
		}
	}

	// Handle registration with email and password
	async function handleRegister(e: React.FormEvent) {
		e.preventDefault()

		try {
			setLoading(true)

			const { error } = await supabase.auth.signUp({
				email,
				password,
				options: { data: { displayName } },
			})

			if (error) {
				throw error
			}

			toast.success(t("auth.registerSuccess"))
			navigate({ to: appConfig.redirects.afterSignUpUrl })
		} catch (e) {
			const error = e as Error
			toast.error(error.message || t("auth.registerFailed"))
			setError(error.message || t("auth.registerFailed"))
		} finally {
			setLoading(false)
		}
	}

	function onTabChange(newTab: string) {
		navigate({ search: () => ({ tab: newTab }) } as any)
	}

	useEffect(() => {
		if (error) {
			const timer = setTimeout(
				() => setError(null),
				appConfig.misc?.toastErrorTimeout || 5000,
			)
			return () => clearTimeout(timer)
		}
	}, [error])

	return (
		<Page className={"bg-primary/30"}>
			<Card className={"w-full max-w-md p-8 pb-12"}>
				<CardHeader className={"items-center gap-2"}>
					<img
						src={"/assets/icons/icon-maskable.png"}
						alt={"logo"}
						width={150}
						height={150}
					/>
					<CardTitle>{AppTitle}</CardTitle>
					<CardDescription>{t("auth.description")}</CardDescription>
				</CardHeader>
				<CardContent className={"flex items-center justify-center"}>
					<Tabs
						value={defaultTab || "login"}
						onValueChange={onTabChange}
						className={"w-full"}
					>
						<TabsList className={"grid w-full grid-cols-2"}>
							<TabsTrigger value={"login"}>{t("auth.login")}</TabsTrigger>
							<TabsTrigger value={"register"}>{t("auth.register")}</TabsTrigger>
						</TabsList>

						{/* Login Tab Content */}
						<TabsContent value={"login"}>
							<form
								className={"flex flex-col gap-6 text-input"}
								onSubmit={handleLogin}
							>
								<div className={"grid gap-6"}>
									<div className={"grid gap-2"}>
										<Label htmlFor={"login-email"}>{t("auth.email")}</Label>
										<Input
											id={"login-email"}
											type={"email"}
											placeholder={"m@example.com"}
											autoComplete={"email"}
											value={email}
											onChange={(e) => setEmail(e.target.value)}
											required
										/>
									</div>
									<div className={"grid gap-2"}>
										<div className={"flex items-center"}>
											<Label htmlFor={"login-password"}>
												{t("auth.password")}
											</Label>
										</div>
										<Input
											id={"login-password"}
											type={"password"}
											autoComplete={"current-password"}
											value={password}
											onChange={(e) => setPassword(e.target.value)}
											required
										/>
									</div>
									{error && (
										<p className={"text-sm text-center text-destructive-600"}>
											{error}
										</p>
									)}
									<Button
										type={"submit"}
										className={"w-full"}
										disabled={loading}
									>
										{loading ? t("auth.loggingIn") : t("auth.login")}
									</Button>
								</div>
							</form>
						</TabsContent>

						{/* Register Tab Content */}
						<TabsContent value={"register"}>
							<form className={"flex flex-col gap-6"} onSubmit={handleRegister}>
								<div className={"grid gap-6"}>
									<div className={"grid gap-2"}>
										<Label htmlFor={"register-email"}>{t("auth.email")}</Label>
										<Input
											id={"register-email"}
											type={"email"}
											placeholder={"m@example.com"}
											autoComplete={"email"}
											value={email}
											onChange={(e) => setEmail(e.target.value)}
											required
										/>
									</div>
									<div className={"grid gap-2"}>
										<Label htmlFor={"register-password"}>
											{t("auth.password")}
										</Label>
										<Input
											id={"register-password"}
											type={"password"}
											autoComplete={"current-password"}
											value={password}
											onChange={(e) => setPassword(e.target.value)}
											required
										/>
									</div>
									<div className={"grid gap-2"}>
										<Label htmlFor={"name"}>{t("auth.name")}</Label>
										<Input
											id={"name"}
											placeholder={"John Doe"}
											value={displayName}
											onChange={(e) => setDisplayName(e.target.value)}
											required
										/>
									</div>
									{error && (
										<p className={"text-sm text-center text-destructive-600"}>
											{error}
										</p>
									)}
									<Button
										type={"submit"}
										className={"w-full"}
										disabled={loading}
									>
										{loading ? t("auth.creatingAccount") : t("auth.register")}
									</Button>
								</div>
							</form>
						</TabsContent>
					</Tabs>
				</CardContent>
			</Card>
		</Page>
	)
}
